import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PublicationsIntro from './PublicationsIntro';

interface Publication {
  title: string;
  abstract: string;
  image: string;
  conference?: string;
  year?: string;
  url?: string;
}

const Publications: React.FC = () => {
  const [showIntro, setShowIntro] = useState(true);
  const publications: Publication[] = [
    {
      title: "Multiscale feature fusion and local detail enhancement-based edge detection",
      abstract: "Fusing edge detection methods gives better balance photo structure and noise impact. Our method's edge result has both low over detection and rich details. In the experiments, a multi-scale feature fusion and local detail enhancement based edge detection method outperforms SOTA.",
      image: "/photo/publication-1.webp",
      conference: "EIEDP 2025",
      url: "https://www.spiedigitallibrary.org/conference-proceedings-of-spie/13574/135740A/Multiscale-feature-fusion-and-local-detail-enhancement-based-edge-detection/10.1117/12.3067041.short"
    },
    {
      title: "Research on Active Learning for NN-based Automatic Speech Recognition",
      abstract: "In recent years, there have been major changes in Automatic Speech Recognition (ASR). And due to the advancements of neural network-based models like DNN and more used in ASR, such as Deep Neural Network, Hidden Markov Model (DNN-HMM) and etc.",
      image: "/photo/publication-2.webp",
      conference: "#IMLCI2021",
      url: "https://ieeexplore.ieee.org/document/9736727"
    }
  ];

  return (
    <AnimatePresence mode="wait">
      {showIntro ? (
        <PublicationsIntro key="intro" onContinue={() => setShowIntro(false)} />
      ) : (
        <motion.section 
          key="publications"
          id="publications" 
          className="relative py-20 px-8 md:px-16 lg:px-24 z-20 scroll-mt-16"
          style={{ backgroundColor: '#2C2721' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-7xl mx-auto">
            <motion.h2 
              className="text-5xl md:text-6xl font-bold mb-16 text-center"
              style={{ 
                fontFamily: 'Vixa-Regular, sans-serif',
                color: '#F1EFE0'
              }}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Publications
            </motion.h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {publications.map((pub, index) => (
                <motion.div
                  key={index}
                  className="bg-[#1a1a1a] rounded-lg overflow-hidden shadow-xl cursor-pointer"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => pub.url && window.open(pub.url, '_blank')}
                >
                  <div className="relative h-auto lg:h-80 overflow-hidden" style={{ backgroundColor: '#1a1a1a' }}>
                    <img 
                      src={pub.image} 
                      alt={pub.title}
                      className="w-full h-auto lg:h-full block object-contain border-0 outline-none"
                      loading="lazy"
                      style={{ border: 'none', outline: 'none' }}
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-3" style={{ color: '#F1EFE0' }}>
                      {pub.title}
                    </h3>
                    {pub.conference && (
                      <span className="inline-block px-3 py-1 text-sm rounded-full mb-4" 
                            style={{ backgroundColor: '#C2442A', color: '#F1EFE0' }}>
                        {pub.conference}
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  );
};

export default Publications;